/**
 * The one-off Bolt Star onboarding dialog, shown once per browser to merchants in the programme.
 *
 * It lives in the design-system folder rather than next to its container in `app/src/widgets/`
 * to be lazy and not deal with tailwind setup. Move this folder to `app/src/widgets/` once the
 * Clay migration is finished and tailwind works across the Portal.
 */
import React from 'react';

import { Button, Dialog, IconButton } from '@bolteu/clay-react';
import { Cross } from '@bolteu/kalep-react-icons';

import illustrationSrc from '../assets/bolt-star-onboarding-illustration.jpg';

type DialogChangeEventDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Dialog.Root>['onOpenChange']>
>[1];

export interface BoltStarOnboardingDialogProps {
  isOpen: boolean;
  /** Fired only by the close button: the backdrop and Escape deliberately do not dismiss. */
  onClose: () => void;
  onDismiss: () => void;
  onAction: () => void;
  title: string;
  description: string;
  actionLabel: string;
  dismissLabel: string;
  closeLabel: string;
  illustrationAlt: string;
}

export function BoltStarOnboardingDialog({
  isOpen,
  onClose,
  onDismiss,
  onAction,
  title,
  description,
  actionLabel,
  dismissLabel,
  closeLabel,
  illustrationAlt,
}: BoltStarOnboardingDialogProps) {
  const handleOpenChange = (open: boolean, eventDetails: DialogChangeEventDetails) => {
    // block closing via shroud or ESC
    if (!open && eventDetails.reason === 'close-press') {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} disablePointerDismissal>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-110 shadow-lg">
          <Dialog.Close
            render={
              <IconButton aria-label={closeLabel} icon={<Cross />} size="sm" variant="inverted" />
            }
            className="absolute right-3 top-2 z-10"
          />
          {/* The whole body scrolls on short viewports instead of squeezing the text, while the
              buttons stay pinned. Only the text is the Description, so screen readers don't
              announce the title twice. */}
          <div className="bolt-font-body-m-regular grow overflow-y-auto overscroll-none pb-2 text-primary">
            <img
              src={illustrationSrc}
              alt={illustrationAlt}
              width={440}
              height={250}
              className="block h-auto w-full"
            />
            <Dialog.Title
              className="bolt-font-heading-m-accent m-0 pb-3 pl-6 pr-4 pt-4"
              // eslint-disable-next-line jsx-a11y/heading-has-content -- content added by title component
              render={<h2 />}
            >
              {title}
            </Dialog.Title>
            <Dialog.Description className="m-0 overflow-visible pb-0 pt-2" render={<p />}>
              {description}
            </Dialog.Description>
          </div>
          <Dialog.Footer className="shrink-0 flex-col gap-2">
            <Button className="w-full" size="md" variant="primary" onClick={onAction}>
              {actionLabel}
            </Button>
            <Button className="w-full" size="md" variant="tertiary" onClick={onDismiss}>
              {dismissLabel}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
